/**
 * Admin Subscriptions Page
 * Allows administrators to manage packages and assign them to users
 * @component AdminSubscriptionsPage
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  Grid,
  Avatar,
  InputAdornment,
  Tabs,
  Tab,
  Tooltip,
  Checkbox,
  TableContainer,
  Table as MuiTable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Pagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  MoreVert as MoreIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  MonetizationOn as MonetizationOnIcon,
  Timeline as TimelineIcon,
  Sync as SyncIcon,
  History as HistoryIcon,
  Security as SecurityIcon,
  Cancel as CancelIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import Layout from '../../common/layout/Layout';
import { Package, getAllPackages, updatePackage, createPackage, syncBusinessStatuses } from '../../services/packageService';
import Table, { Column } from '../../components/Table';
import { styles } from './styles';

interface CreatePackageData {
  name: string;
  description: string;
  price: number;
  currency: string;
  max_businesses: number;
  billing_period: string;
  billing_interval: number;
  target_audience: string;
}

const AdminSubscriptionsPage: React.FC = () => {
  console.log('🚀 AdminSubscriptionsPage component rendered');
  const { language } = useLanguage();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Subscriptions state
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionsError, setSubscriptionsError] = useState<string | null>(null);
  const [subscriptionPage, setSubscriptionPage] = useState(1);
  const [subscriptionTotalCount, setSubscriptionTotalCount] = useState(0);
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState('');
  const [subscriptionSearchFilter, setSubscriptionSearchFilter] = useState('');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [cancelSubscriptionDialogOpen, setCancelSubscriptionDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<any | null>(null);

  // Form states
  const [createFormData, setCreateFormData] = useState<CreatePackageData>({
    name: '',
    description: '',
    price: 0,
    currency: 'USD',
    max_businesses: 0,
    billing_period: 'monthly',
    billing_interval: 1,
    target_audience: 'masters'
  });

  const [editFormData, setEditFormData] = useState<CreatePackageData>({
    name: '',
    description: '',
    price: 0,
    currency: 'USD',
    max_businesses: 1,
    billing_period: 'monthly',
    billing_interval: 1,
    target_audience: 'masters'
  });

  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [tabValue, setTabValue] = useState(0);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    if (tabValue === 2) { // Client Subscriptions tab
      fetchSubscriptions();
    }
  }, [tabValue, subscriptionPage, subscriptionStatusFilter, subscriptionSearchFilter]);

  const fetchSubscriptions = async () => {
    try {
      setSubscriptionsLoading(true);
      setSubscriptionsError(null);

      const params = new URLSearchParams({
        page: subscriptionPage.toString(),
        limit: '20',
      });

      if (subscriptionStatusFilter) {
        params.append('status', subscriptionStatusFilter);
      }

      if (subscriptionSearchFilter) {
        params.append('search', subscriptionSearchFilter);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/admin/subscriptions?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch subscriptions: ${response.statusText}`);
      }

      const data = await response.json();
      setSubscriptions(data.subscriptions);
      setSubscriptionTotalCount(data.pagination.totalCount);
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
      setSubscriptionsError(language === 'ua' ? 'Не вдалося завантажити підписки' : 'Failed to load subscriptions');
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!selectedSubscription) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/admin/subscriptions/${selectedSubscription.id}/cancel`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: selectedSubscription.user_id,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to cancel subscription: ${response.statusText}`);
      }

      setCancelSubscriptionDialogOpen(false);
      setSelectedSubscription(null);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Підписку скасовано успішно' : 'Subscription cancelled successfully',
        severity: 'success',
      });

      fetchSubscriptions(); // Refresh the list
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка скасування підписки' : 'Failed to cancel subscription',
        severity: 'error',
      });
    }
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllPackages();
      setPackages(data);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
      setError('Не вдалося завантажити пакети');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePackage = async () => {
    console.log('🔄 handleCreatePackage called');
    console.log('📝 Current form data:', createFormData);

    // Form validation
    if (!createFormData.name.trim()) {
      console.log('❌ Validation failed: Package name is empty');
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Назва пакета обов\'язкова' : 'Package name is required',
        severity: 'error'
      });
      return;
    }

    if (createFormData.price < 0) {
      console.log('❌ Validation failed: Price is negative', createFormData.price);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Ціна не може бути від\'ємною' : 'Price cannot be negative',
        severity: 'error'
      });
      return;
    }

    if (createFormData.max_businesses < 0) {
      console.log('❌ Validation failed: Max businesses is negative', createFormData.max_businesses);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Кількість бізнесів не може бути від\'ємною' : 'Max businesses cannot be negative',
        severity: 'error'
      });
      return;
    }

    console.log('✅ Form validation passed');

    try {
      console.log('📡 Calling createPackage API with data:', createFormData);

      const result = await createPackage(createFormData);
      console.log('✅ API call successful, result:', result);

      setCreateDialogOpen(false);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Пакет створено успішно' : 'Package created successfully',
        severity: 'success'
      });

      console.log('🔄 Fetching updated packages list');
      await fetchPackages();

      // Reset form
      console.log('🔄 Resetting form data');
      setCreateFormData({
        name: '',
        description: '',
        price: 0,
        currency: 'USD',
        max_businesses: 1,
        billing_period: 'monthly',
        billing_interval: 1,
        target_audience: 'masters'
      });

      console.log('🎉 Package creation completed successfully');

    } catch (err) {
      console.error('❌ Failed to create package:', err);

      // More detailed error logging
      if (err instanceof Error) {
        console.error('❌ Error message:', err.message);
        console.error('❌ Error stack:', err.stack);
      }

      setSnackbar({
        open: true,
        message: language === 'ua'
          ? `Помилка створення пакета: ${err instanceof Error ? err.message : 'Невідома помилка'}`
          : `Failed to create package: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error'
      });
    }
  };

  const handleEditPackage = async () => {
    if (!selectedPackage) return;

    try {
      await updatePackage(selectedPackage.id, {
        name: editFormData.name,
        description: editFormData.description,
        price: editFormData.price,
        currency: editFormData.currency,
        max_businesses: editFormData.max_businesses
      });

      setEditDialogOpen(false);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Пакет оновлено успішно' : 'Package updated successfully',
        severity: 'success'
      });
      fetchPackages();
    } catch (err) {
      console.error('Failed to update package:', err);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка оновлення пакета' : 'Failed to update package',
        severity: 'error'
      });
    }
  };

  const handleDeletePackage = async (packageId: string) => {
    if (!confirm('Ви впевнені, що хочете видалити цей пакет?')) return;

    try {
      // TODO: Implement delete package API call
      console.log('Deleting package:', packageId);
      setSnackbar({
        open: true,
        message: 'Пакет видалено успішно',
        severity: 'success'
      });
      fetchPackages();
    } catch (err) {
      console.error('Failed to delete package:', err);
      setSnackbar({
        open: true,
        message: 'Помилка видалення пакета',
        severity: 'error'
      });
    }
  };



  const openEditDialog = (pkg: Package) => {
    setSelectedPackage(pkg);
    setEditFormData({
      name: pkg.name,
      description: pkg.description,
      price: pkg.price,
      currency: pkg.currency,
      max_businesses: pkg.max_businesses,
      billing_period: pkg.billing_period || 'monthly',
      billing_interval: pkg.billing_interval || 1,
      target_audience: pkg.target_audience || 'masters'
    });
    setEditDialogOpen(true);
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat(language === 'ua' ? 'uk-UA' : 'en-US', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  const getBusinessLimitText = (limit: number) => {
    return limit.toString();
  };

  const handleSyncBusinessStatuses = async () => {
    try {
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Починаємо синхронізацію...' : 'Starting synchronization...',
        severity: 'info'
      });

      const result = await syncBusinessStatuses();

      setSnackbar({
        open: true,
        message: language === 'ua'
          ? `Синхронізація завершена: ${result.message}`
          : `Synchronization completed: ${result.message}`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Failed to sync business statuses:', err);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка синхронізації статусів бізнесів' : 'Failed to sync business statuses',
        severity: 'error'
      });
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6">Завантаження...</Typography>
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            <Tooltip title={error} placement="top">
              <span style={{ cursor: 'pointer' }}>
                Не вдалося заванта...
              </span>
            </Tooltip>
          </Alert>
        </Box>
      </Layout>
    );
  }

  // Define permissions table columns and data
  const permissionsColumns: Column<{
    role: string;
    createBusiness: boolean;
    editBusiness: boolean;
    deleteBusiness: boolean;
    manageServices: boolean;
    manageEmployees: boolean;
    viewAnalytics: boolean;
    manageClients: boolean;
    viewAppointments: boolean;
    manageAppointments: boolean;
    accessSupport: boolean;
  }>[] = [
    {
      key: 'role',
      label: language === 'ua' ? 'Роль / Дозвіл' : 'Role / Permission',
      minWidth: '200px'
    },
    {
      key: 'createBusiness',
      label: language === 'ua' ? 'Створити бізнес' : 'Create Business',
      align: 'center',
      minWidth: '120px',
      render: (value: boolean) => (
        <Checkbox checked={value} disabled />
      )
    },
    {
      key: 'editBusiness',
      label: language === 'ua' ? 'Редагувати бізнес' : 'Edit Business',
      align: 'center',
      minWidth: '120px',
      render: (value: boolean) => (
        <Checkbox checked={value} disabled />
      )
    },
    {
      key: 'deleteBusiness',
      label: language === 'ua' ? 'Видалити бізнес' : 'Delete Business',
      align: 'center',
      minWidth: '120px',
      render: (value: boolean) => (
        <Checkbox checked={value} disabled />
      )
    },
    {
      key: 'manageServices',
      label: language === 'ua' ? 'Керувати послугами' : 'Manage Services',
      align: 'center',
      minWidth: '120px',
      render: (value: boolean) => (
        <Checkbox checked={value} />
      )
    },
    {
      key: 'manageEmployees',
      label: language === 'ua' ? 'Керувати співробітниками' : 'Manage Employees',
      align: 'center',
      minWidth: '120px',
      render: (value: boolean) => (
        <Checkbox checked={value} disabled />
      )
    },
    {
      key: 'viewAnalytics',
      label: language === 'ua' ? 'Перегляд аналітики' : 'View Analytics',
      align: 'center',
      minWidth: '120px',
      render: (value: boolean) => (
        <Checkbox checked={value} disabled />
      )
    },
    {
      key: 'manageClients',
      label: language === 'ua' ? 'Керувати клієнтами' : 'Manage Clients',
      align: 'center',
      minWidth: '120px',
      render: (value: boolean) => (
        <Checkbox checked={value} disabled />
      )
    },
    {
      key: 'viewAppointments',
      label: language === 'ua' ? 'Перегляд записів' : 'View Appointments',
      align: 'center',
      minWidth: '120px',
      render: (value: boolean) => (
        <Checkbox checked={value} />
      )
    },
    {
      key: 'manageAppointments',
      label: language === 'ua' ? 'Керувати записами' : 'Manage Appointments',
      align: 'center',
      minWidth: '120px',
      render: (value: boolean) => (
        <Checkbox checked={value} />
      )
    },
    {
      key: 'accessSupport',
      label: language === 'ua' ? 'Доступ до підтримки' : 'Access Support',
      align: 'center',
      minWidth: '120px',
      render: (value: boolean) => (
        <Checkbox checked={value} />
      )
    }
  ];

  const permissionsData = [
    {
      role: language === 'ua' ? 'Клієнт (Client)' : 'Client',
      createBusiness: false,
      editBusiness: false,
      deleteBusiness: false,
      manageServices: false,
      manageEmployees: false,
      viewAnalytics: false,
      manageClients: true,
      viewAppointments: true,
      manageAppointments: true,
      accessSupport: true
    },
    {
      role: language === 'ua' ? 'Майстер (Master)' : 'Master',
      createBusiness: true,
      editBusiness: true,
      deleteBusiness: false,
      manageServices: true,
      manageEmployees: false,
      viewAnalytics: false,
      manageClients: true,
      viewAppointments: true,
      manageAppointments: true,
      accessSupport: true
    },
    {
      role: language === 'ua' ? 'Власник (Owner)' : 'Owner',
      createBusiness: true,
      editBusiness: true,
      deleteBusiness: true,
      manageServices: true,
      manageEmployees: true,
      viewAnalytics: true,
      manageClients: true,
      viewAppointments: true,
      manageAppointments: true,
      accessSupport: true
    },
    {
      role: language === 'ua' ? 'Адміністратор (Admin)' : 'Admin',
      createBusiness: false,
      editBusiness: false,
      deleteBusiness: false,
      manageServices: false,
      manageEmployees: false,
      viewAnalytics: false,
      manageClients: false,
      viewAppointments: false,
      manageAppointments: false,
      accessSupport: true
    }
  ];

  // Filter packages based on search and status
  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (pkg.description && pkg.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && pkg.is_active) ||
                         (statusFilter === 'inactive' && !pkg.is_active);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="admin-subscriptions-page">
      {/* Page Header - як в AdminBusinessesPage */}
      <div className="page-header content-wrapper full-width">
        <Box sx={styles.pageHeader}>
          <Box>
            <Typography variant="h5" sx={styles.pageTitle}>
              <BusinessIcon sx={{ color: 'primary.main' }} />
              {language === 'ua' ? 'Управління пакетами' : 'Package Management'}
            </Typography>
            <Typography sx={styles.pageSubtitle}>
              {language === 'ua' ? 'Створюйте та керуйте тарифними планами' : 'Create and manage subscription plans'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            size="large"
            sx={styles.permissionsButton}
          >
            {language === 'ua' ? 'Новий пакет' : 'New Package'}
          </Button>
        </Box>

        {/* Tabs */}
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={styles.tabsContainer}>
          <Tab
            icon={<BusinessIcon />}
            iconPosition="start"
            label={language === 'ua' ? 'Пакети' : 'Packages'}
            sx={styles.tabItem}
          />
          <Tab
            icon={<SecurityIcon />}
            iconPosition="start"
            label={language === 'ua' ? 'Дозволи ролей' : 'Role Permissions'}
            sx={styles.tabItem}
          />
          <Tab
            icon={<TimelineIcon />}
            iconPosition="start"
            label={language === 'ua' ? 'Підписки клієнтів' : 'Client Subscriptions'}
            sx={styles.tabItem}
          />
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label={language === 'ua' ? 'Логи' : 'Logs'}
            sx={styles.tabItem}
          />
        </Tabs>
      </div>

      {/* Packages Tab Content */}
      {tabValue === 0 && (
        <Box sx={styles.packagesContent}>
          {/* Search and Filters */}
          <Box sx={styles.searchFilters}>
            <TextField
              placeholder={language === 'ua' ? 'Пошук пакетів...' : 'Search packages...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={styles.searchField}
              size="small"
            />

            <FormControl size="small" sx={styles.statusFilter}>
              <InputLabel>{language === 'ua' ? 'Статус' : 'Status'}</InputLabel>
              <Select
                value={statusFilter}
                label={language === 'ua' ? 'Статус' : 'Status'}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              >
                <MenuItem value="all">{language === 'ua' ? 'Всі' : 'All'}</MenuItem>
                <MenuItem value="active">{language === 'ua' ? 'Активні' : 'Active'}</MenuItem>
                <MenuItem value="inactive">{language === 'ua' ? 'Неактивні' : 'Inactive'}</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary">
              {language === 'ua' ? `Знайдено: ${filteredPackages.length}` : `Found: ${filteredPackages.length}`}
            </Typography>
          </Box>

          {/* Packages Grid */}
          <Grid container spacing={3}>
            {filteredPackages.map((pkg) => (
              <Grid item xs={12} sm={6} lg={4} key={pkg.id}>
                <Card sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                    borderColor: 'primary.main'
                  },
                  border: pkg.is_active ? '2px solid #4caf50' : '1px solid #e0e0e0'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: pkg.is_active ? 'success.main' : 'grey.400' }}>
                          <BusinessIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {pkg.name}
                          </Typography>
                          <Chip
                            label={pkg.is_active ? (language === 'ua' ? 'Активний' : 'Active') : (language === 'ua' ? 'Неактивний' : 'Inactive')}
                            color={pkg.is_active ? 'success' : 'default'}
                            size="small"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Box>
                      </Box>
                      <IconButton size="small">
                        <MoreIcon />
                      </IconButton>
                    </Box>

                    {pkg.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {pkg.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {language === 'ua' ? 'Ціна:' : 'Price:'}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatPrice(pkg.price, pkg.currency)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {language === 'ua' ? 'Бізнесів:' : 'Businesses:'}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {getBusinessLimitText(pkg.max_businesses)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      onClick={() => openEditDialog(pkg)}
                      startIcon={<EditIcon />}
                      sx={{
                        minWidth: { xs: 'auto', sm: 'auto' },
                        px: { xs: 1, sm: 2 }
                      }}
                    >
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        {language === 'ua' ? 'Редагувати' : 'Edit'}
                      </Box>
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleDeletePackage(pkg.id)}
                      startIcon={<DeleteIcon />}
                      sx={{
                        minWidth: { xs: 'auto', sm: 'auto' },
                        px: { xs: 1, sm: 2 }
                      }}
                    >
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        {language === 'ua' ? 'Видалити' : 'Delete'}
                      </Box>
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}

            {filteredPackages.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{
                  textAlign: 'center',
                  py: 8,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: '2px dashed #bdbdbd'
                }}>
                  <BusinessIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    {language === 'ua' ? 'Пакетів не знайдено' : 'No packages found'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {language === 'ua' ? 'Створіть перший пакет або змініть фільтри пошуку' : 'Create your first package or adjust search filters'}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      <Layout fullWidth={true}>
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
          {tabValue === 1 && (
            /* Role Permissions Tab */
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SecurityIcon />
                      {language === 'ua' ? 'Управління дозволами ролей' : 'Role Permissions Management'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {language === 'ua'
                        ? 'Налаштуйте дозволи для різних ролей користувачів системи'
                        : 'Configure permissions for different user roles in the system'
                      }
                    </Typography>

                    {/* Permissions Matrix */}
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table columns={permissionsColumns} data={permissionsData} />
                    </Box>

                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        sx={{ px: 3, py: 1.5 }}
                      >
                        {language === 'ua' ? 'Зберегти зміни' : 'Save Changes'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        sx={{ px: 3, py: 1.5 }}
                      >
                        {language === 'ua' ? 'Скинути до початкового' : 'Reset to Default'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tabValue === 2 && (
            /* Client Subscriptions Tab */
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <PersonIcon />
                {language === 'ua' ? 'Управління підписками клієнтів' : 'Client Subscription Management'}
              </Typography>

              {/* Search and Filters */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                  placeholder={language === 'ua' ? 'Пошук за email або назвою пакета...' : 'Search by email or package name...'}
                  value={subscriptionSearchFilter}
                  onChange={(e) => setSubscriptionSearchFilter(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{ minWidth: 300 }}
                />

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>{language === 'ua' ? 'Статус' : 'Status'}</InputLabel>
                  <Select
                    value={subscriptionStatusFilter}
                    label={language === 'ua' ? 'Статус' : 'Status'}
                    onChange={(e) => setSubscriptionStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">{language === 'ua' ? 'Всі' : 'All'}</MenuItem>
                    <MenuItem value="active">{language === 'ua' ? 'Активні' : 'Active'}</MenuItem>
                    <MenuItem value="cancelled">{language === 'ua' ? 'Скасовані' : 'Cancelled'}</MenuItem>
                    <MenuItem value="expired">{language === 'ua' ? 'Завершені' : 'Expired'}</MenuItem>
                    <MenuItem value="pending">{language === 'ua' ? 'Очікуючі' : 'Pending'}</MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  {language === 'ua' ? `Знайдено: ${subscriptionTotalCount}` : `Found: ${subscriptionTotalCount}`}
                </Typography>
              </Box>

              {/* Subscriptions Table */}
              {subscriptionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <Typography>{language === 'ua' ? 'Завантаження...' : 'Loading...'}</Typography>
                </Box>
              ) : subscriptionsError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {subscriptionsError}
                </Alert>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <MuiTable stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{language === 'ua' ? 'Клієнт' : 'Client'}</TableCell>
                        <TableCell>{language === 'ua' ? 'Пакет' : 'Package'}</TableCell>
                        <TableCell>{language === 'ua' ? 'Сума' : 'Amount'}</TableCell>
                        <TableCell>{language === 'ua' ? 'Статус' : 'Status'}</TableCell>
                        <TableCell>{language === 'ua' ? 'Автопоновлення' : 'Auto Renewal'}</TableCell>
                        <TableCell>{language === 'ua' ? 'Наст. платіж' : 'Next Charge'}</TableCell>
                        <TableCell>{language === 'ua' ? 'Створено' : 'Created'}</TableCell>
                        <TableCell>{language === 'ua' ? 'Дії' : 'Actions'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subscriptions.map((subscription) => (
                        <TableRow key={subscription.id}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {subscription.user_email}
                              </Typography>
                              {subscription.user_full_name && (
                                <Typography variant="caption" color="text.secondary">
                                  {subscription.user_full_name}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {subscription.package_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {subscription.package_description}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatPrice(subscription.amount, subscription.currency)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={subscription.status}
                              color={
                                subscription.status === 'active' ? 'success' :
                                subscription.status === 'cancelled' ? 'error' :
                                subscription.status === 'expired' ? 'warning' : 'default'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Checkbox checked={subscription.auto_renewal} disabled size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {subscription.next_charge_date ? 
                                new Date(subscription.next_charge_date).toLocaleDateString() : 
                                language === 'ua' ? 'Н/Д' : 'N/A'
                              }
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(subscription.created_at).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {subscription.status === 'active' && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedSubscription(subscription);
                                  setCancelSubscriptionDialogOpen(true);
                                }}
                                title={language === 'ua' ? 'Скасувати підписку' : 'Cancel subscription'}
                              >
                                <CancelIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </MuiTable>
                </TableContainer>
              )}

              {/* Pagination */}
              {subscriptionTotalCount > 20 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={Math.ceil(subscriptionTotalCount / 20)}
                    page={subscriptionPage}
                    onChange={(_, value) => setSubscriptionPage(value)}
                    color="primary"
                  />
                </Box>
              )}
            </Box>
          )}

          {tabValue === 3 && (
            /* Analytics Tab */
            <Grid container spacing={3}>
              {/* Revenue Chart */}
              <Grid item xs={12} lg={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MonetizationOnIcon />
                      {language === 'ua' ? 'Дохід по пакетах' : 'Revenue by Package'}
                    </Typography>
                    <Box sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      borderRadius: 1
                    }}>
                      <Typography color="text.secondary">
                        {language === 'ua' ? 'Графік доходів буде додано' : 'Revenue chart coming soon'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Key Metrics */}
              <Grid item xs={12} lg={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUpIcon />
                      {language === 'ua' ? 'Ключові метрики' : 'Key Metrics'}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                          <Typography variant="h4" color="primary.contrastText" fontWeight={600}>
                            {packages.length}
                          </Typography>
                          <Typography variant="caption" color="primary.contrastText">
                            {language === 'ua' ? 'Пакетів' : 'Packages'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h4" color="success.contrastText" fontWeight={600}>
                            {packages.filter(p => p.is_active).length}
                          </Typography>
                          <Typography variant="caption" color="success.contrastText">
                            {language === 'ua' ? 'Активних' : 'Active'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                          <Typography variant="h4" color="warning.contrastText" fontWeight={600}>
                            {packages.reduce((sum, pkg) => sum + pkg.price, 0).toFixed(0)}
                          </Typography>
                          <Typography variant="caption" color="warning.contrastText">
                            {language === 'ua' ? 'Загальна вартість' : 'Total Value'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                          <Typography variant="h4" color="info.contrastText" fontWeight={600}>
                            {packages.reduce((sum, pkg) => sum + pkg.max_businesses, 0)}
                          </Typography>
                          <Typography variant="caption" color="info.contrastText">
                            {language === 'ua' ? 'Бізнесів' : 'Businesses'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Usage Stats */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupIcon />
                      {language === 'ua' ? 'Статистика використання' : 'Usage Statistics'}
                    </Typography>
                    <Box sx={{
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      borderRadius: 1
                    }}>
                      <Typography color="text.secondary">
                        {language === 'ua' ? 'Статистика використання буде додана' : 'Usage statistics coming soon'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tabValue === 2 && (
            /* Logs Tab */
            <Grid container spacing={3}>
              {/* Processing Actions */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SyncIcon />
                      {language === 'ua' ? 'Обробка даних' : 'Data Processing'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {language === 'ua'
                        ? 'Інструменти для синхронізації та обробки даних пакетів та бізнесів'
                        : 'Tools for synchronizing and processing package and business data'
                      }
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        startIcon={<SyncIcon />}
                        onClick={handleSyncBusinessStatuses}
                        size="large"
                        sx={{ px: 3, py: 1.5 }}
                      >
                        {language === 'ua' ? 'Синхронізувати статуси бізнесів' : 'Sync Business Statuses'}
                      </Button>

                      <Button
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        onClick={() => {
                          setSnackbar({
                            open: true,
                            message: language === 'ua' ? 'Історія операцій буде додана' : 'Operation history coming soon',
                            severity: 'info'
                          });
                        }}
                        size="large"
                        sx={{ px: 3, py: 1.5 }}
                      >
                        {language === 'ua' ? 'Переглянути історію' : 'View History'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Processing Results */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon />
                      {language === 'ua' ? 'Результати обробки' : 'Processing Results'}
                    </Typography>
                    <Box sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '2px dashed #bdbdbd'
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <HistoryIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          {language === 'ua' ? 'Немає результатів обробки' : 'No processing results'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {language === 'ua'
                            ? 'Використовуйте кнопки вище для запуску операцій обробки'
                            : 'Use the buttons above to start processing operations'
                          }
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}



        {/* Create Package Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {language === 'ua' ? 'Створити новий пакет' : 'Create New Package'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={language === 'ua' ? 'Назва пакета' : 'Package Name'}
              value={createFormData.name}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={language === 'ua' ? 'Опис' : 'Description'}
              value={createFormData.description}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, description: e.target.value }))}
              margin="normal"
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                label={language === 'ua' ? 'Ціна' : 'Price'}
                type="number"
                value={createFormData.price}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ minWidth: 100 }}>
                <InputLabel>{language === 'ua' ? 'Валюта' : 'Currency'}</InputLabel>
                <Select
                  value={createFormData.currency}
                  label={language === 'ua' ? 'Валюта' : 'Currency'}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, currency: e.target.value }))}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="UAH">UAH</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label={language === 'ua' ? 'Максимальна кількість бізнесів' : 'Max Businesses'}
              type="number"
              value={createFormData.max_businesses}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, max_businesses: parseInt(e.target.value) || 0 }))}
              margin="normal"
              helperText={language === 'ua' ? '0 = необмежено' : '0 = unlimited'}
              InputProps={{ inputProps: { min: 0 } }}
            />
            {/* Billing Period */}
            <FormControl fullWidth margin="normal">
              <InputLabel>{language === 'ua' ? 'Період оплати' : 'Billing Period'}</InputLabel>
              <Select
                value={createFormData.billing_period}
                label={language === 'ua' ? 'Період оплати' : 'Billing Period'}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, billing_period: e.target.value as 'monthly' | 'yearly' }))}
              >
                <MenuItem value="monthly">{language === 'ua' ? 'Щомісячно' : 'Monthly'}</MenuItem>
                <MenuItem value="yearly">{language === 'ua' ? 'Щорічно' : 'Yearly'}</MenuItem>
              </Select>
            </FormControl>

            {/* Billing Interval */}
            <TextField
              fullWidth
              label={language === 'ua' ? 'Інтервал оплати' : 'Billing Interval'}
              type="number"
              value={createFormData.billing_interval}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, billing_interval: parseInt(e.target.value) || 1 }))}
              margin="normal"
              helperText={language === 'ua'
                ? createFormData.billing_period === 'monthly'
                  ? 'Кількість місяців між платежами'
                  : 'Кількість років між платежами'
                : createFormData.billing_period === 'monthly'
                  ? 'Number of months between payments'
                  : 'Number of years between payments'
              }
              InputProps={{ inputProps: { min: 1 } }}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>{language === 'ua' ? 'Цільова аудиторія' : 'Target Audience'}</InputLabel>
              <Select
                value={createFormData.target_audience}
                label={language === 'ua' ? 'Цільова аудиторія' : 'Target Audience'}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, target_audience: e.target.value }))}
              >
                <MenuItem value="clients">{language === 'ua' ? 'Клієнти' : 'Clients'}</MenuItem>
                <MenuItem value="masters">{language === 'ua' ? 'Майстри' : 'Masters'}</MenuItem>
                <MenuItem value="owners">{language === 'ua' ? 'Власники' : 'Owners'}</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>
              {language === 'ua' ? 'Скасувати' : 'Cancel'}
            </Button>
            <Button
              onClick={() => {
                console.log('🎯 Create button clicked');
                handleCreatePackage();
              }}
              variant="contained"
            >
              {language === 'ua' ? 'Створити' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Package Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {language === 'ua' ? 'Редагувати пакет' : 'Edit Package'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={language === 'ua' ? 'Назва пакета' : 'Package Name'}
              value={editFormData.name}
              onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={language === 'ua' ? 'Опис' : 'Description'}
              value={editFormData.description}
              onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
              margin="normal"
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                label={language === 'ua' ? 'Ціна' : 'Price'}
                type="number"
                value={editFormData.price}
                onChange={(e) => setEditFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ minWidth: 100 }}>
                <InputLabel>{language === 'ua' ? 'Валюта' : 'Currency'}</InputLabel>
                <Select
                  value={editFormData.currency}
                  label={language === 'ua' ? 'Валюта' : 'Currency'}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, currency: e.target.value }))}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="UAH">UAH</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label={language === 'ua' ? 'Максимальна кількість бізнесів' : 'Max Businesses'}
              type="number"
              value={editFormData.max_businesses}
              onChange={(e) => setEditFormData(prev => ({ ...prev, max_businesses: parseInt(e.target.value) || 0 }))}
              margin="normal"
              InputProps={{ inputProps: { min: 0 } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>
              {language === 'ua' ? 'Скасувати' : 'Cancel'}
            </Button>
            <Button onClick={handleEditPackage} variant="contained">
              {language === 'ua' ? 'Зберегти' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assign Package Dialog */}
        <Dialog
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {language === 'ua' ? 'Призначити пакет користувачу' : 'Assign Package to User'}
          </DialogTitle>
          <DialogContent>
            {selectedPackage && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6">{selectedPackage.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatPrice(selectedPackage.price, selectedPackage.currency)} •
                  {getBusinessLimitText(selectedPackage.max_businesses)} {language === 'ua' ? 'бізнесів' : 'businesses'}
                </Typography>
              </Box>
            )}

            {/* TODO: Add user search and selection component */}
            <Typography color="text.secondary">
              {language === 'ua'
                ? 'Функціонал призначення пакетів користувачам буде додано після реалізації пошуку користувачів'
                : 'Package assignment functionality will be added after user search implementation'
              }
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignDialogOpen(false)}>
              {language === 'ua' ? 'Закрити' : 'Close'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Subscription Dialog */}
        <Dialog
          open={cancelSubscriptionDialogOpen}
          onClose={() => setCancelSubscriptionDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {language === 'ua' ? 'Скасувати підписку' : 'Cancel Subscription'}
          </DialogTitle>
          <DialogContent>
            {selectedSubscription && (
              <Box>
                <Typography variant="body1" gutterBottom>
                  {language === 'ua' 
                    ? `Ви впевнені, що хочете скасувати підписку користувача ${selectedSubscription.user_email} на пакет "${selectedSubscription.package_name}"?`
                    : `Are you sure you want to cancel the subscription for user ${selectedSubscription.user_email} to package "${selectedSubscription.package_name}"?`
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {language === 'ua' 
                    ? 'Ця дія призведе до негайного припинення автоподовлення підписки. Користувач отримає сповіщення про скасування.'
                    : 'This action will immediately stop the subscription auto-renewal. The user will be notified about the cancellation.'
                  }
                </Typography>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {language === 'ua' ? 'Деталі підписки:' : 'Subscription Details:'}
                  </Typography>
                  <Typography variant="body2">
                    {language === 'ua' ? 'Клієнт:' : 'Client:'} {selectedSubscription.user_email}
                  </Typography>
                  <Typography variant="body2">
                    {language === 'ua' ? 'Пакет:' : 'Package:'} {selectedSubscription.package_name}
                  </Typography>
                  <Typography variant="body2">
                    {language === 'ua' ? 'Сума:' : 'Amount:'} {formatPrice(selectedSubscription.amount, selectedSubscription.currency)}
                  </Typography>
                  <Typography variant="body2">
                    {language === 'ua' ? 'Наступний платіж:' : 'Next Charge:'} {selectedSubscription.next_charge_date ? 
                      new Date(selectedSubscription.next_charge_date).toLocaleDateString() : 
                      language === 'ua' ? 'Н/Д' : 'N/A'
                    }
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelSubscriptionDialogOpen(false)}>
              {language === 'ua' ? 'Назад' : 'Back'}
            </Button>
            <Button 
              onClick={handleCancelSubscription}
              color="error"
              variant="contained"
            >
              {language === 'ua' ? 'Скасувати підписку' : 'Cancel Subscription'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
    </div>
  );
};

export default AdminSubscriptionsPage;
